import { Formik } from 'formik'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { SignUp } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { SignUpFormValues } from './SignUpForm.interface'
import { SignUpSchema } from './SignUpForm.validator'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import tw from 'twin.macro'
import parsePhoneNumber from 'libphonenumber-js'
import { InfoContainer } from 'src/components/shared/InfoContainer'
import { TOS } from 'src/components/shared/TOS'
import { handleApiError } from 'src/utils/errorHandlers'
import { CLIENT_ROUTES } from 'src/constants/routes'
import { ALREADY_REGISTERED_ERROR } from 'src/constants/auth'
import { Button } from 'src/components/shared/Button'
import { FormikCheckbox } from 'src/components/shared/form/formik/FormikCheckbox'

interface Props {
  partnerName: string
  partnerMarketId: string
  referral?: string
  phone?: string
  email?: string
  firstName?: string
  lastName?: string
  onboardingTemplateId: string
  tagNames: string[]
  onSuccess(phone: string): void
}

const PHONE_MASK = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]

export const SignUpForm = ({
  partnerName,
  partnerMarketId,
  onboardingTemplateId,
  tagNames,
  referral,
  phone,
  email,
  firstName,
  lastName,
  onSuccess,
}: Props) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleFormSubmit = async (values: SignUpFormValues) => {
    setIsLoading(true)
    setError(null)

    try {
      const parsedPhone = parsePhoneNumber(values.phone, 'US')

      const phone = parsedPhone?.formatInternational().replace(/\s+/g, '') || values.phone

      const params: any = {
        phone,
        partnerName,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        partnerMarketId,
        onboardingTemplateId,
      }

      if (values.referral) {
        params.referral = values.referral
      }

      if (tagNames.length) {
        params.tagNames = tagNames
      }

      await createAsyncAction(dispatch, SignUp.request(params))

      onSuccess(phone)
    } catch (err: any) {
      if (err === ALREADY_REGISTERED_ERROR) {
        navigate(
          `${CLIENT_ROUTES.ALREADY_REGISTERED_URL}?partnerName=${encodeURIComponent(partnerName)}`,
        )
      } else {
        handleApiError(err)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const initValues = useMemo<SignUpFormValues>(
    () => ({
      phone: phone || '',
      email: email || '',
      firstName: firstName || '',
      lastName: lastName || '',
      referral: referral || '',
      acceptOptIn: false,
    }),
    [phone, email, firstName, lastName, referral],
  )

  return (
    <Formik initialValues={initValues} onSubmit={handleFormSubmit} validationSchema={SignUpSchema}>
      {({ handleSubmit }) => (
        <div>
          <FormikTextField
            name="firstName"
            css={tw`w-full mb-4`}
            label="First name*"
            placeholder="Enter your first name"
          />
          <FormikTextField
            name="lastName"
            css={tw`w-full mb-4`}
            label="Last name*"
            placeholder="Enter your last name"
          />
          <FormikTextField
            name="email"
            css={tw`w-full mb-8`}
            label="Email address*"
            placeholder="Enter your email address"
          />
          <FormikTextField
            name="phone"
            css={tw`w-full mb-4`}
            mask={PHONE_MASK}
            label="Phone number*"
            placeholder="Enter your phone number"
          />
          <InfoContainer className={tw`mb-10`}>
            We will send a verification code to the phone number you provided.
          </InfoContainer>
          <FormikTextField
            name="referral"
            css={tw`w-full`}
            label="If you were referred by someone, please let us know their name (otherwise leave blank)"
            placeholder="Enter referral name"
          />
          <div css={tw`mb-4 mt-6`}>
            <FormikCheckbox
              css={tw`w-full`}
              name="acceptOptIn"
              label="By providing your phone number, you agree to receive SMS messages about your account. Message & data rates may apply."
            />
          </div>
          <TOS css={tw`mb-6 mt-10`} />
          <FormErrorMessage css={tw`mb-4`} error={error} />
          <Button className={tw`w-full`} loading={isLoading} onClick={() => handleSubmit()}>
            Start onboarding
          </Button>
        </div>
      )}
    </Formik>
  )
}
